import React, { useEffect, useMemo, useState } from 'react';
import ServiceCard from '../service-card/service-card';
import './delivery-service-selection.scss';
import { DealerProfileInfo } from '../../../../../models/osb-model/osb-dealer-info';
import { OSBContentData } from '../../../../../models/osb-model/osb-content-data';
import { useDealerServiceWithOfflineRetry } from '../../../../../hooks/osb-v3/useDealerServiceWithOfflineRetry';
import { ServiceInfo } from '../../../../../models/osb-model/osb-dealerservice-info';
import { FMButton } from '@own/fds-react';
import { DeliveryServiceSkeleton } from './delivery-service-skeleton';

interface DeliveryServiceSelectionProps {
    dealer: DealerProfileInfo;
    contentData: OSBContentData;
    onNext: (selectedServiceId: string) => void;
    onBack: () => void;
}

export const DeliveryServiceSelection: React.FC<DeliveryServiceSelectionProps> = ({
    dealer,
    contentData,
    onNext,
    onBack,
}) => {
    const {
        deliveryServices,
        loadInitialServicesAndRetryMains,
        httpState,
    } = useDealerServiceWithOfflineRetry();

    useEffect(() => {
        loadInitialServicesAndRetryMains(dealer, contentData);
    }, [loadInitialServicesAndRetryMains, dealer, contentData]);

    const [selectedServiceId, setSelectedServiceId] = useState<string | null>(
        null
    );
    useEffect(() => {
        if (deliveryServices.length > 0 && selectedServiceId === null) {
            const defaultDropOff = deliveryServices.find(service =>
                service.convenienceOptionTypes?.includes('DropOff')
            );

            if (defaultDropOff) {
                setSelectedServiceId(defaultDropOff.serviceId.toString());
            } else {
                setSelectedServiceId(deliveryServices[0].serviceId.toString());
            }
        }
    }, [deliveryServices, selectedServiceId]);
    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedServiceId(event.target.value);
    };

    const handleContinue = () => {
        if (selectedServiceId) {
            onNext(selectedServiceId);
        }
    };

    const {
        collectionServices,
        dropOffServices,
        otherServices,
    } = useMemo(() => {
        const collection: ServiceInfo[] = [];
        const dropOff: ServiceInfo[] = [];
        const others: ServiceInfo[] = [];

        deliveryServices.forEach(service => {
            const types = service.convenienceOptionTypes || [];

            if (types.includes('PickUpAndDelivery')) {
                collection.push(service);
            } else if (types.includes('DropOff')) {
                dropOff.push(service);
            } else {
                others.push(service);
            }
        });

        return {
            collectionServices: collection,
            dropOffServices: dropOff,
            otherServices: others,
        };
    }, [deliveryServices]);

    if (httpState.isLoading) {
        return <DeliveryServiceSkeleton />;
    }

    if (httpState.error) {
        return (
            <div className="osb-v3-delivery-selection">
                <p>
                    {contentData?.apiErrorMessage}: {httpState.error}
                </p>
            </div>
        );
    }

    const isContinueDisabled = selectedServiceId === null;

    // --- 1. Define Collection Section JSX ---
    const collectionSection = collectionServices.length > 0 && (
        <div className="osb-v3-delivery-selection__group">
            <h2 className="osb-v3-delivery-selection__group-title">
                {contentData?.pickupSectionTitle}
            </h2>
            {collectionServices.map(service => (
                <ServiceCard
                    key={service.serviceId}
                    id={service.serviceId.toString()}
                    inputType="radio"
                    name="delivery-option"
                    serviceName={service.name}
                    infoTooltipText={service.description}
                    serviceDisclaimer={
                        service.additionalInfo?.serviceDisclaimer
                    }
                    offerText={service.additionalInfo?.offer}
                    price={service.price}
                    checked={selectedServiceId === service.serviceId.toString()}
                    onChange={handleRadioChange}
                />
            ))}
        </div>
    );

    // --- 2. Define DropOff Section JSX ---
    const dropOffSection = dropOffServices.length > 0 && (
        <div className="osb-v3-delivery-selection__group">
            <h2 className="osb-v3-delivery-selection__group-title">
                {contentData?.dropoffSectionTitle}
            </h2>
            {dropOffServices.map(service => (
                <ServiceCard
                    key={service.serviceId}
                    id={service.serviceId.toString()}
                    inputType="radio"
                    name="delivery-option"
                    serviceName={service.name}
                    infoTooltipText={service.description}
                    serviceDisclaimer={
                        service.additionalInfo?.serviceDisclaimer
                    }
                    offerText={service.additionalInfo?.offer}
                    price={service.price}
                    checked={selectedServiceId === service.serviceId.toString()}
                    onChange={handleRadioChange}
                />
            ))}
        </div>
    );

    // --- 3. Determine Order based on Config ---
    const showPickupFirst = contentData?.showPickupSectionFirst === true;

    return (
        <div className="osb-v3-delivery-selection">
            <div className="osb-v3-delivery-selection__header">
                <div className="osb-v3-delivery-selection__back-button-wrapper">
                    <FMButton
                        chevron="left"
                        onClick={onBack}
                        label={contentData?.backButtonText}
                        type="tertiary"
                        data-testid="back-button"
                    />
                </div>
                <h1 className="osb-v3-delivery-selection__title">
                    {contentData?.deliveryHeaderTitle}
                </h1>
                <p className="osb-v3-delivery-selection__subtitle">
                    {contentData?.deliveryHeaderDescription}
                </p>
            </div>

            {showPickupFirst ? (
                <>
                    {collectionSection}
                    {dropOffSection}
                </>
            ) : (
                <>
                    {dropOffSection}
                    {collectionSection}
                </>
            )}

            {/* --- Group 3: Other Services (Always at the bottom) --- */}
            {otherServices.length > 0 && (
                <div className="osb-v3-delivery-selection__group">
                    {otherServices.map(service => (
                        <ServiceCard
                            key={service.serviceId}
                            id={service.serviceId.toString()}
                            inputType="radio"
                            name="delivery-option"
                            serviceName={service.name}
                            infoTooltipText={service.description}
                            serviceDisclaimer={
                                service.additionalInfo?.serviceDisclaimer
                            }
                            offerText={service.additionalInfo?.offer}
                            price={service.price}
                            checked={
                                selectedServiceId ===
                                service.serviceId.toString()
                            }
                            onChange={handleRadioChange}
                        />
                    ))}
                </div>
            )}

            {!httpState.isLoading && deliveryServices.length === 0 && (
                <p>No delivery options available for this dealer.</p>
            )}

            <div className="osb-v3-delivery-selection__actions">
                <FMButton
                    href=""
                    label={contentData?.continueButtonText}
                    disabled={isContinueDisabled}
                    theme="light"
                    type="primary"
                    onClick={handleContinue}
                />
            </div>
        </div>
    );
};
